
import { getRandomWarmTip } from "../utils/getRandomWarmTip";
import type { UserRealtimeSummary, SummaryCounts } from "./dailySummaryService";

// Types
export type SummaryUserKey = "kevin" | "lisa" | "irene";

export interface TodoItem {
  id: string; // This is FlowID
  module: string;
  title: string;
  docNo?: string;
  todoStatus: string; 
  updatedAt?: any;
  status?: string; 
  
  currentOwnerName?: string;
  assigneeUserKey?: string; 
  currentStageKey?: string; 
  dashboardBucket?: string; // Contract, Seal, Compliance, Governance, Case, Template, Other

  entityId?: string; 
  _derivationReason?: string; 
  [key: string]: any;
}

export interface ComputedSummary {
  generatedAt: string;
  users: UserRealtimeSummary[];
  byUser: Record<SummaryUserKey, UserRealtimeSummary>;
  unknown: TodoItem[]; 
  meta: {
    totalActive: number;
    unknownCount: number;
    source: "TODOS";
  };
}

function createEmptyUser(key: SummaryUserKey, name: string): UserRealtimeSummary {
  return {
    userKey: key,
    displayName: name,
    // templates added to initial counts and pendingList
    counts: { contracts: 0, seals: 0, compliance: 0, governance: 0, cases: 0, templates: 0, others: 0, total: 0 },
    dateLabel: new Date().toLocaleDateString(),
    agenda: [],
    dueSoon: { today: [], within30: [], within60: [] },
    pendingList: { contracts: [], seals: [], cases: [], compliance: [], governance: [], templates: [], others: [] }, 
    encouragement: getRandomWarmTip()
  };
}

export function computeDashboardSummaryFromTodos(todos: TodoItem[]): ComputedSummary {
  const users: Record<SummaryUserKey, UserRealtimeSummary> = {
    kevin: createEmptyUser("kevin", "Kevin (法務)"),
    lisa: createEmptyUser("lisa", "Lisa (主管)"),
    irene: createEmptyUser("irene", "Irene (主管)")
  };

  const unknownList: TodoItem[] = [];
  
  // Filter for OPEN todos
  const activeTodos = todos.filter(t => t.todoStatus === "OPEN");

  activeTodos.forEach(todo => {
      const keyRaw = (todo.assigneeUserKey || "").toUpperCase();
      let userKey: SummaryUserKey | null = null;
      
      if (keyRaw === 'KEVIN') userKey = 'kevin';
      else if (keyRaw === 'LISA') userKey = 'lisa';
      else if (keyRaw === 'IRENE') userKey = 'irene';
      
      if (userKey) {
          const bucket = (todo.dashboardBucket || "Other");
          const targetUser = users[userKey];
          
          targetUser.counts.total++;

          const displayItem = {
             id: todo.entityId || todo.id,
             title: todo.title || "(無標題)",
             module: todo.module,
             docNo: todo.docNo,
             currentStage: todo.currentStageKey || 'Unknown',
             flowStatus: todo.overallStatus || todo.status,
             reason: `[${bucket}] ${todo.currentStageKey}`
          };

          // Handling different dashboard buckets including Template
          if (bucket === 'Contract') {
             targetUser.counts.contracts++;
             targetUser.pendingList.contracts.push(displayItem as any);
          } else if (bucket === 'Seal') {
             targetUser.counts.seals++;
             targetUser.pendingList.seals.push(displayItem as any);
          } else if (bucket === 'Compliance') {
             targetUser.counts.compliance++;
             targetUser.pendingList.compliance.push(displayItem as any);
          } else if (bucket === 'Governance') {
             targetUser.counts.governance++;
             targetUser.pendingList.governance.push(displayItem as any);
          } else if (bucket === 'Case') {
             targetUser.counts.cases++;
             targetUser.pendingList.cases.push(displayItem as any);
          } else if (bucket === 'Template') {
             targetUser.counts.templates++;
             targetUser.pendingList.templates.push(displayItem as any);
          } else {
             targetUser.counts.others++;
             targetUser.pendingList.others.push(displayItem as any);
          }

      } else {
          // If unmapped, add to unknown
          if (keyRaw !== 'DEPT' && keyRaw !== 'NONE') { 
             unknownList.push(todo);
          }
      }
  });

  return {
    generatedAt: new Date().toISOString(),
    users: [users.kevin, users.lisa, users.irene], 
    byUser: users, 
    unknown: unknownList,
    meta: {
      totalActive: activeTodos.length,
      unknownCount: unknownList.length,
      source: "TODOS"
    }
  };
}

export function computeDashboardSummary(anyData: any[]) {
    return computeDashboardSummaryFromTodos(anyData as TodoItem[]);
}
